/* CareerClarityStart.jsx — Lean entry point for the After-10th / Inter quiz.
 *
 * Per spec: signed-up users for `after_10th_inter` land here DIRECTLY (no
 * menu, no previews). ONE tap ("Engineering / Business / Not sure") → quiz.
 *
 * Engineering    → /career-clarity/btech
 * Business       → /career-clarity/bba-mba
 * Not sure yet   → /career-clarity/btech    (blended default; spec says
 *                                            "start with a blended/default set")
 */

import React, { useEffect, useState } from 'react';
import PathwayDisclaimer from '../components/PathwayDisclaimer';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Cpu, Briefcase, Compass, Loader2, GraduationCap, ArrowRight, BookOpen } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { HERO_STAGES } from '../components/Hero';
import { HERO_CARD_COVERS } from '../data/heroCardCovers';
import SpecIntroVideo, { introSeen, markIntroSeen } from '../components/SpecIntroVideo';
import { getIntroVideo } from '../data/introVideos';

const INTRO_KEY = 'after_10th_inter';
const API = process.env.REACT_APP_BACKEND_URL;

const TRACKS = [
  { id: 'btech',   route: '/career-clarity/btech',   label: 'Engineering / Tech',      icon: Cpu,       desc: 'B.Tech · CSE · ECE · Mechanical · Civil & more.' },
  { id: 'bba_mba', route: '/career-clarity/bba-mba', label: 'Business / Management',   icon: Briefcase, desc: 'BBA · MBA · Marketing · Finance · HR · Ops.' },
  { id: 'blended', route: '/career-clarity/btech',   label: 'Not sure yet',            icon: Compass,   desc: "Start with a blended set — we'll narrow it down." },
];

// Legacy test flow — kept for future use, intentionally not rendered.
export function LegacyCareerClarityStart() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resuming, setResuming] = useState(true);
  const [showIntro, setShowIntro] = useState(false);
  const [introChecked, setIntroChecked] = useState(false);

  useEffect(() => {
    setShowIntro(!introSeen(INTRO_KEY, user));
    setIntroChecked(true);
  }, [user]);

  /* On entry: if the user already has a finished report on EITHER track,
     take them straight to it instead of asking again. Server-side: we can
     check both attempts endpoints. If any attempts exist, route to the
     latest track. */
  useEffect(() => {
    if (!user || user === false || user.is_guest) { setResuming(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const [bt, bb] = await Promise.all([
          axios.get(`${API}/api/paths/after_10th_inter/attempts?track=btech`).catch(() => null),
          axios.get(`${API}/api/paths/after_10th_inter/attempts?track=bba_mba`).catch(() => null),
        ]);
        if (cancelled) return;
        const btUsed = bt?.data?.used || 0;
        const bbUsed = bb?.data?.used || 0;
        // If they have any attempts on a track, jump them to that track —
        // the quiz page knows whether to show the report or the retake CTA.
        if (btUsed > 0 && bbUsed === 0) return navigate('/career-clarity/btech', { replace: true });
        if (bbUsed > 0 && btUsed === 0) return navigate('/career-clarity/bba-mba', { replace: true });
        if (btUsed > 0 && bbUsed > 0) {
          // Both tracks tried — go to the latest one
          const btTime = bt?.data?.latest?.created_at || '';
          const bbTime = bb?.data?.latest?.created_at || '';
          return navigate(btTime > bbTime ? '/career-clarity/btech' : '/career-clarity/bba-mba',
                          { replace: true });
        }
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, navigate]);

  if (resuming || !introChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600"/>
      </div>
    );
  }

  if (showIntro) {
    const v = getIntroVideo(INTRO_KEY);
    return <SpecIntroVideo videoId={v.videoId} eyebrow={v.eyebrow}
      onDone={() => { markIntroSeen(INTRO_KEY, user); setShowIntro(false); }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50" data-testid="career-clarity-start">
      <Navbar/>
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-3xl mx-auto">

          {/* Rewatch link */}
          <div className="mb-4 flex justify-end">
            <button type="button" onClick={() => setShowIntro(true)}
              className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 hover:text-teal-900"
              data-testid="cc-watch-intro-btn">
              ▶ Watch intro
            </button>
          </div>

          {/* ── AFTER 10TH SECTION (new, completely separate) ─────────── */}
          <section className="mb-14" data-testid="cc-after-10th-section">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex w-7 h-7 rounded-full bg-amber-100 text-amber-700 items-center justify-center">
                <GraduationCap size={16}/>
              </span>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-amber-700">
                After 10th · pick your group
              </p>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Just finished 10th? Check which group really fits you.
            </h2>
            <p className="text-sm text-gray-600 mt-2 mb-5 leading-relaxed">
              Five official Intermediate groups — MPC, BiPC, MEC, CEC, HEC.
              Open any, take its 18-question fit quiz, get an honest report.
            </p>
            <Link
              to="/career-clarity/after-10th"
              data-testid="cc-after-10th-cta"
              className="group inline-flex items-center justify-between gap-3 w-full md:w-auto bg-white border-2 border-amber-200 rounded-2xl px-5 py-4 hover:border-amber-400 hover:shadow-md transition-all"
            >
              <span className="flex items-center gap-3">
                <span className="inline-flex w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white items-center justify-center font-extrabold">5</span>
                <span className="text-left">
                  <span className="block font-bold text-gray-900">Open the 5 groups</span>
                  <span className="block text-xs text-gray-500">MPC · BiPC · MEC · CEC · HEC</span>
                </span>
              </span>
              <ArrowRight size={18} className="text-gray-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all"/>
            </Link>
          </section>

          {/* ── AFTER INTER SECTION (existing — UNCHANGED) ────────────── */}
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex w-7 h-7 rounded-full bg-teal-100 text-teal-700 items-center justify-center">
              <Briefcase size={16}/>
            </span>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-teal-700">
              After Inter · choose your direction
            </p>
          </div>
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-2">
              Career Clarity · Test 1 of 4
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3"
                data-testid="cc-start-headline">
              Quick one — which feels closer to you?
            </h1>
            <p className="text-gray-600 text-sm max-w-xl mx-auto">
              Pick one. We&apos;ll start your quiz right after.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            {TRACKS.map((t, i) => (
              <motion.button
                key={t.id}
                type="button"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07, duration: 0.4, ease: 'easeOut' }}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate(t.route)}
                className="bg-white border-2 border-gray-200 rounded-2xl p-5 text-left hover:border-teal-400 hover:shadow-md transition-all"
                data-testid={`cc-track-${t.id}`}
              >
                <t.icon className="w-7 h-7 text-teal-600 mb-3"/>
                <div className="font-bold text-gray-900 mb-1 text-base leading-tight">
                  {t.label}
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">{t.desc}</p>
              </motion.button>
            ))}
          </div>

          <Link
            to="/career-clarity/after-inter/guide"
            data-testid="cc-after-inter-guide-cta"
            className="group mt-6 flex items-center justify-between gap-3 w-full bg-white border-2 border-teal-200 rounded-2xl px-5 py-4 hover:border-teal-400 hover:shadow-md transition-all"
          >
            <span className="flex items-start gap-3 min-w-0">
              <span className="inline-flex w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 text-white items-center justify-center"><BookOpen size={18}/></span>
              <span className="text-left min-w-0">
                <span className="block font-bold text-gray-900 leading-snug">After Class 12 / Inter: Study, Skills &amp; Career Clarity</span>
                <span className="block text-xs text-gray-600 mt-0.5 leading-relaxed">Routes, qualification check, skilled work, apprenticeships, money checks, your state&rsquo;s official links and an interest-exploration quiz.</span>
              </span>
            </span>
            <ArrowRight size={18} className="text-gray-400 shrink-0 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all"/>
          </Link>

          <p className="text-center text-xs text-gray-400 mt-8" data-testid="cc-start-foot">
            4 short tests · short theory after each · final decision report.
          </p>
          <PathwayDisclaimer />
        </div>
      </div>
    </div>
  );
}

const CC_PATHS = [
  ['specializations', 'Inner Specializations'],
  ['job-roles', 'Future-Ready Job Roles'],
  ['ai-pathway', 'Future AI Agent Pathway'],
  ['internships', 'Internship Pathfinder'],
  ['blue-collar', 'Blue-Collar Jobs'],
  ['ownpath', 'Entrepreneurship — OwnPath'],
  ['agriculture', 'Agriculture'],
  ['life-skills', 'Life Skills — For Every Future'],
  ['the-ai-shift', 'The AI Shift — Prepare Now'],
];

function ClarityPathCard({ s, title }) {
  const { isDark } = useTheme();
  const cover = HERO_CARD_COVERS.find((c) => c.to === s.to);
  return (
    <div className={`flex flex-col bg-white rounded-3xl border-2 overflow-hidden ${s.border}`} data-testid={`cc-card-${s.key}`}>
      {cover && (
        <div className="w-full" style={{ aspectRatio: '3 / 2', background: isDark ? '#12161D' : '#FFF8EC' }}>
          <img src={isDark ? cover.src.replace('/cards/', '/cards/dark/') : cover.src} alt={cover.alt} className="w-full h-full object-contain block" loading="lazy" decoding="async" />
        </div>
      )}
      <div className="flex flex-col flex-1 p-5">
        <h2 className="font-bold text-gray-900 text-lg leading-snug">{title}</h2>
        <p className="text-sm text-gray-600 mt-1 mb-4 leading-relaxed" data-testid={`cc-tagline-${s.key}`}>{s.sub}</p>
        <Link to={s.to} data-testid={`cc-explore-${s.key}`}
          className="mt-auto self-start inline-flex items-center gap-2 min-h-[44px] px-5 rounded-full bg-teal-600 text-white text-sm font-bold hover:bg-teal-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2">
          Explore <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

export default function CareerClarityStart() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 overflow-x-hidden" data-testid="career-clarity-start">
      <Navbar/>
      <div className="pt-28 pb-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900" data-testid="cc-heading">Career Clarity</h1>
          <p className="mt-3 inline-block text-[11px] font-extrabold uppercase tracking-[0.18em] text-teal-700 bg-teal-100 px-3 py-1 rounded" data-testid="cc-audience">After Class 12 / Intermediate • 18+</p>
          <p className="mt-5 max-w-3xl text-base text-gray-700 leading-relaxed" data-testid="cc-intro">
            Before choosing a degree or career, explore your options. AI is changing jobs and skills, while the future impact of more advanced AI and hypothetical superintelligence (SI) remains uncertain. Explore the nine pathways below, understand what interests you, and prepare through learning and practice. Discuss your choices, costs and circumstances with your family before deciding.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-testid="cc-grid">
            {CC_PATHS.map(([key, title]) => {
              const s = HERO_STAGES.find((x) => x.key === key);
              return s ? <ClarityPathCard key={key} s={s} title={title} /> : null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
